import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { DEPOSIT_METHODS, RECEPTION_METHODS, CORRIDORS, STATUS_LABEL, SUPPORT_NUMBERS, formatAmount, formatDate } from "@/lib/constants";
import BrandLogo from "@/components/BrandLogos";
import { StatusProgressBar } from "@/components/ActiveTransactionBanner";
import CopyRefButton from "@/components/CopyRefButton";
import ImageLightbox from "@/components/ImageLightbox";
import { Search, CheckCircle2, Clock, MessageCircle, Copy, Check, FileImage, ArrowRight, Download } from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Track() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("code") || "";
  const [code, setCode] = useState(initial);
  const [tx, setTx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { src, alt, isPdf }

  const search = async (c) => {
    if (!c) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/transactions/track/${c.trim().toUpperCase()}`);
      setTx(data);
      setParams({ code: c.trim().toUpperCase() });
    } catch (e) {
      setTx(null);
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initial) search(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh the transaction every 30s while it's in progress (and tab visible).
  useEffect(() => {
    if (!tx?.code) return;
    const inProgress = ["en_attente", "verifie", "envoye"].includes(tx.status);
    if (!inProgress) return;
    let lastStatus = tx.status;
    const tick = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const { data } = await api.get(`/transactions/track/${tx.code}`);
        if (data && data.status !== lastStatus) {
          lastStatus = data.status;
          setTx(data);
          if (data.status === "termine") {
            toast.success(`${data.code} · Transaction terminée`, { description: "Le bénéficiaire a bien été payé." });
          } else if (data.status === "annule") {
            toast.error(`${data.code} · Transaction annulée`);
          } else {
            toast.success(`${data.code} · Statut mis à jour`, { description: STATUS_LABEL[data.status] });
          }
        } else if (data) {
          setTx(data);
        }
      } catch (_e) { /* ignore */ }
    };
    const id = setInterval(tick, 30000);
    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVisible); };
  }, [tx?.code, tx?.status]);

  return (
    <div className="min-h-screen sl-hero-bg">
      <Header />
      <div className="pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--sl-secondary-dark)]">Suivi</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-2">Suivez votre transaction</h1>
          <p className="text-gray-600 mt-2">Entrez votre identifiant de transaction (ex: SL-XXXXXXXX).</p>

          <div className="mt-6 flex gap-2">
            <input
              className="sl-input flex-1 font-mono uppercase"
              placeholder="SL-XXXXXXXX"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && search(code)}
              data-testid="track-input"
            />
            <button onClick={() => search(code)} disabled={loading || !code} className="sl-btn-primary" data-testid="track-search-btn">
              <Search size={16} /> Chercher
            </button>
          </div>

          {tx && (
            <div className="mt-10 sl-card" data-testid="tx-detail-card">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="text-xs uppercase tracking-widest text-gray-500">Référence</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-mono text-2xl font-bold" data-testid="tx-code-display">{tx.code}</span>
                    <CopyRefButton code={tx.code} />
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {CORRIDORS[tx.corridor || "tg_ga"]?.flagFrom} {CORRIDORS[tx.corridor || "tg_ga"]?.label} {CORRIDORS[tx.corridor || "tg_ga"]?.flagTo}
                  </div>
                </div>
                <StatusBadge status={tx.status} cancellationReason={tx.cancellation_reason} />
              </div>

              {/* Visual progress bar — visible immediately, updates with status */}
              <div className="mt-6 rounded-2xl bg-[var(--sl-bg-alt)] p-5 sm:p-6" data-testid="track-progress-bar">
                <StatusProgressBar status={tx.status} />
              </div>

              {tx.status === "termine" && (
                <button
                  type="button"
                  onClick={() => setLightbox({
                    src: `${API_BASE}/transactions/${tx.code}/receipt`,
                    alt: "Reçu de transaction",
                    downloadName: `recu-${tx.code}.pdf`,
                    isPdf: true,
                  })}
                  className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[var(--sl-primary)] text-white px-5 py-3 font-semibold hover:brightness-110 transition"
                  data-testid="download-receipt-btn"
                >
                  <Download size={18} /> Télécharger le reçu (PDF)
                </button>
              )}

              {tx.cancellation_reason === "kyc_rejected" && (
                <div
                  className="mt-5 rounded-xl border-2 border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 flex items-start gap-2"
                  data-testid="kyc-rejected-banner"
                >
                  <span className="font-semibold mt-0.5">❌</span>
                  <div>
                    <div className="font-semibold">Pièce d'identité refusée — transaction annulée</div>
                    <div className="mt-1 text-red-800">
                      Votre pièce d'identité n'a pas pu être validée par notre équipe.
                      {tx.kyc_rejection_reason && (
                        <>
                          {" "}
                          <span className="font-semibold">Motif :</span> {tx.kyc_rejection_reason}
                        </>
                      )}
                    </div>
                    <div className="mt-2 text-xs">
                      Vous pouvez relancer une nouvelle transaction avec une pièce d'identité plus lisible. Notre équipe reste à votre disposition pour toute question.
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 grid md:grid-cols-3 gap-4 text-center">
                <Box label="Vous envoyez" value={formatAmount(tx.amount_xof, tx.source_currency || "XOF")} />
                <Box label="Frais" value={formatAmount(tx.fee, tx.source_currency || "XOF")} accent />
                <Box label={`Reçu ${(tx.corridor || "tg_ga") === "ga_fr" ? "en France" : "au Gabon"}`} value={formatAmount(tx.amount_xaf, tx.target_currency || "XAF")} primary />
              </div>

              <div className="mt-6 grid md:grid-cols-2 gap-6 text-sm">
                <div>
                  <h4 className="font-heading font-semibold mb-2">Expéditeur</h4>
                  <div className="text-gray-700">{tx.sender_name}</div>
                  <div className="text-gray-500">{tx.sender_phone}</div>
                  <div className="mt-3 flex items-center gap-2 text-gray-500">
                    <BrandLogo method={tx.deposit_method} size={28} />
                    <span>Dépôt : <span className="font-medium text-gray-700">{DEPOSIT_METHODS[tx.deposit_method]?.label}</span></span>
                  </div>
                  {tx.payment_reference && (
                    <div className="text-gray-500 mt-1">Réf. paiement : <span className="font-mono text-gray-700">{tx.payment_reference}</span></div>
                  )}
                </div>
                <div>
                  <h4 className="font-heading font-semibold mb-2">Bénéficiaire</h4>
                  <div className="text-gray-700">{tx.beneficiary_name}</div>
                  {tx.beneficiary_phone && <div className="text-gray-500">{tx.beneficiary_phone}</div>}
                  {tx.beneficiary_email && <div className="text-gray-500">{tx.beneficiary_email}</div>}
                  {tx.beneficiary_account && <div className="text-gray-500 font-mono text-xs break-all">{tx.beneficiary_account}</div>}
                  <div className="mt-3 flex items-center gap-2 text-gray-500">
                    <BrandLogo method={tx.reception_method} size={28} />
                    <span>Réception : <span className="font-medium text-gray-700">{RECEPTION_METHODS[tx.reception_method]?.label}</span></span>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <h4 className="font-heading font-semibold mb-3">Chronologie</h4>
                <ol className="space-y-3">
                  {(tx.timeline || []).map((t, i) => (
                    <li key={i} className="flex items-start gap-3" data-testid={`timeline-item-${i}`}>
                      <div className="mt-1 w-7 h-7 rounded-full grid place-items-center bg-[var(--sl-primary)]/10 text-[var(--sl-primary)]">
                        {t.status === "termine" ? <CheckCircle2 size={16} /> : <Clock size={14} />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{STATUS_LABEL[t.status]}</span>
                          <span className="text-xs text-gray-400">par {t.by}</span>
                        </div>
                        <div className="text-xs text-gray-500">{formatDate(t.at)}</div>
                        {t.reason && <div className="text-xs text-amber-700 mt-1">Raison : {t.reason}</div>}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Notify agent via WhatsApp (only useful while pending) */}
              {(tx.status === "en_attente" || tx.status === "verifie") && (
                <NotifyAgentSection tx={tx} />
              )}

              {/* Payout proof shown after transaction is completed — proof for the client that the beneficiary has received funds */}
              {tx.payout_proof_path && tx.status === "termine" && (
                <div className="mt-8" data-testid="payout-proof-public">
                  <h4 className="font-heading font-semibold mb-3 flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-emerald-600" /> Preuve du paiement au bénéficiaire
                  </h4>
                  <button
                    type="button"
                    onClick={() => setLightbox({
                      src: `${API_BASE}/transactions/${tx.code}/payout-proof`,
                      alt: "Preuve de paiement au bénéficiaire",
                    })}
                    className="block group w-full text-left"
                    data-testid="payout-proof-open"
                  >
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/transactions/${tx.code}/payout-proof`}
                      alt="Preuve de paiement"
                      className="max-h-[400px] rounded-xl border bg-white object-contain mx-auto group-hover:opacity-90 transition cursor-zoom-in"
                      style={{ borderColor: "var(--sl-border)" }}
                    />
                    <div className="mt-2 text-xs text-gray-500 text-center">Capture du paiement effectué par l'agent · cliquez pour agrandir</div>
                  </button>
                </div>
              )}

              {/* Proof image (publicly visible to anyone with the transaction code — agent sees it via WhatsApp link) */}
              {tx.proof_path && (
                <div className="mt-8" data-testid="proof-block">
                  <h4 className="font-heading font-semibold mb-3 flex items-center gap-2">
                    <FileImage size={18} className="text-[var(--sl-primary)]" />
                    Preuve de dépôt
                  </h4>
                  <button
                    type="button"
                    onClick={() => setLightbox({
                      src: `${API_BASE}/transactions/${tx.code}/proof`,
                      alt: "Capture du dépôt",
                    })}
                    className="block group w-full text-left"
                    data-testid="proof-open"
                  >
                    <img
                      src={`${API_BASE}/transactions/${tx.code}/proof`}
                      alt="Capture du dépôt"
                      className="max-h-[420px] rounded-xl border bg-white object-contain mx-auto group-hover:opacity-90 transition cursor-zoom-in"
                      style={{ borderColor: "var(--sl-border)" }}
                      data-testid="proof-image"
                    />
                    <div className="mt-2 text-xs text-gray-500 text-center">Cliquez pour agrandir</div>
                  </button>
                </div>
              )}

              {/* End-of-transaction CTAs — always visible once a tx is loaded */}
              <div
                className="mt-10 pt-8 border-t border-gray-100 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between"
                data-testid="tx-end-actions"
              >
                <div className="text-sm text-gray-500">
                  {tx.status === "termine"
                    ? "🎉 Votre transfert est terminé. Merci d'avoir choisi Swiftlink-Africa !"
                    : tx.status === "annule"
                    ? "Transaction annulée. Vous pouvez relancer un nouvel envoi à tout moment."
                    : "Conservez votre référence pour suivre votre transfert plus tard."}
                </div>
                <div className="flex flex-wrap gap-3">
                  {tx.status === "en_attente" && (
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Annuler la transaction ${tx.code} ? Cette action est irréversible.`)) return;
                        try {
                          const { data } = await api.post(`/transactions/${tx.code}/cancel`);
                          setTx(data.transaction);
                          toast.success("Transaction annulée");
                        } catch (e) {
                          toast.error(formatApiError(e?.response?.data?.detail));
                        }
                      }}
                      className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium border border-red-300 text-red-700 hover:bg-red-50 transition"
                      data-testid="client-cancel-btn"
                    >
                      Annuler ma transaction
                    </button>
                  )}
                  <Link to="/envoyer" className="sl-btn-primary" data-testid="tx-end-send-again">
                    Effectuer un autre transfert <ArrowRight size={16} />
                  </Link>
                  <Link to="/" className="sl-btn-outline" data-testid="tx-end-home">
                    Retour à l'accueil
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
      {lightbox && (
        <ImageLightbox
          src={lightbox.src}
          alt={lightbox.alt}
          downloadHref={lightbox.src}
          downloadName={lightbox.downloadName}
          isPdf={!!lightbox.isPdf}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

function Box({ label, value, primary, accent }) {
  const cls = primary ? "text-[var(--sl-primary)]" : accent ? "text-amber-700" : "text-gray-900";
  return (
    <div className="rounded-xl bg-[var(--sl-bg-alt)] p-4">
      <div className="text-xs uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`font-heading text-xl font-bold mt-1 ${cls}`}>{value}</div>
    </div>
  );
}

function NotifyAgentSection({ tx }) {
  const c = CORRIDORS[tx.corridor || "tg_ga"];
  const [copied, setCopied] = useState(false);
  // Lien protégé pour l'agent : il devra entrer son code 6 chiffres pour voir les détails.
  const agentViewUrl = `${window.location.origin}/agent/voir?code=${tx.code}`;
  const lines = [
    `🔔 Swiftlink-Africa — Nouveau dépôt à vérifier`,
    `Réf : ${tx.code}`,
    `Corridor : ${c?.label}`,
    `Montant : ${formatAmount(tx.amount_xof, tx.source_currency || "XOF")}`,
    `Expéditeur : ${tx.sender_name} (${tx.sender_phone})`,
    `Bénéficiaire : ${tx.beneficiary_name}${tx.beneficiary_phone ? " · " + tx.beneficiary_phone : ""}`,
    `Méthode dépôt : ${DEPOSIT_METHODS[tx.deposit_method]?.label || tx.deposit_method}`,
    `🔐 Voir la transaction (code agent requis) : ${agentViewUrl}`,
    ``,
    `Merci de vérifier le dépôt et valider la transaction.`,
  ].filter(Boolean);
  const messageText = lines.join("\n");
  const message = encodeURIComponent(messageText);

  const waNumber = (raw) => raw.replace(/\D/g, "");

  const copyRecap = async () => {
    try {
      await navigator.clipboard.writeText(messageText);
      setCopied(true);
      toast.success("Récap copié dans le presse-papiers");
      setTimeout(() => setCopied(false), 1800);
    } catch (_e) {
      toast.error("Impossible de copier — sélectionnez le texte manuellement");
    }
  };

  return (
    <div className="mt-8 rounded-2xl border-2 border-dashed p-5" style={{ borderColor: "#25D366" }} data-testid="notify-agent-section">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full grid place-items-center flex-shrink-0" style={{ background: "#25D366" }}>
          <MessageCircle size={18} className="text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h4 className="font-heading font-semibold">Avertir l'agent que votre dépôt est effectué</h4>
              <p className="text-sm text-gray-600 mt-1">
                Cliquez sur votre pays pour ouvrir WhatsApp avec un message déjà rempli contenant tous les détails de votre transaction. L'agent vérifiera et validera plus rapidement.
              </p>
            </div>
            <button
              type="button"
              onClick={copyRecap}
              className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full border bg-white hover:bg-gray-50 transition flex-shrink-0"
              style={{ borderColor: "var(--sl-border)" }}
              data-testid="copy-recap-btn"
            >
              {copied ? <><Check size={14} className="text-emerald-600" /> Copié</> : <><Copy size={14} /> Copier le récap</>}
            </button>
          </div>
          <div className="mt-4 grid sm:grid-cols-3 gap-2">
            {SUPPORT_NUMBERS.map((s) => (
              <a
                key={s.country}
                href={`https://wa.me/${waNumber(s.phone)}?text=${message}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 p-3 rounded-lg border bg-white hover:border-[#25D366] transition"
                style={{ borderColor: "var(--sl-border)" }}
                data-testid={`notify-${s.country.toLowerCase()}`}
              >
                <span className="text-xl">{s.flag}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900">Agent {s.country}</div>
                  <div className="text-[11px] text-gray-500 font-mono">{s.phone}</div>
                </div>
                <MessageCircle size={14} className="text-[#25D366] flex-shrink-0" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
